import React, { useEffect, useRef, useState } from 'react';
import PartLabelPrint from './PartLabelPrint';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AddIncoBarTagForm from '../../forms/AddIncoBarTag';
import { postData } from '../../api/ApiServices';
import CustomAlert from '../common/alert/CustomAlert';
import '../../styles/GrnTableStyles.css';
import '../../styles/incobartag.css';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/slices/store';

const IncoBarTag = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const labelsRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [tempLabelsData, setTempLabelsData] = useState<any[]>([]);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => { }
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => { }
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const permission = useSelector((state: RootState) => state.permissions);

  const handlePrintLabels = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one barcode item to print');
      return;
    }

    setIsPrinting(true);

    const selectedItems = tableData.filter(item =>
      selectedRows.includes(item._id || item.tagNumber)
    );

    const newLabels = selectedItems.map(item => {
      const partDetails = item.partId?.part;

      return {
        partNo: partDetails?.internalPartNo || "N/A",
        partName: partDetails?.partName || "N/A",
        model: partDetails?.modelId?.name || "N/A",
        batchNo: new Date().toLocaleDateString('en-GB'),
        invNo: item.grnId.number || "N/A",
        grnNo: item.grnId?.grnNo || "N/A",
        supplier: item.supplierId?.name || "N/A",
        barcode: item.tagNumber,
        receivedDate: new Date().toLocaleString('en-GB'),
        category: partDetails?.categoryId?.name || "N/A",
        rack: item.rackId.rackName || "N/A",
        qty: item.qty || 0,
      };
    });

    setTempLabelsData(newLabels);

    setTimeout(async () => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const element = labelsRef.current;

      if (!element) {
        setIsPrinting(false);
        setTempLabelsData([]);
        return;
      }

      const labelElements = element.querySelectorAll('.label-container-incobar');
      console.log(`Selected ${selectedRows.length} items, rendering ${labelElements.length} labels`);

      for (let i = 0; i < labelElements.length; i += 2) {
        const canvases = [];

        for (let j = i; j < i + 2 && j < labelElements.length; j++) {
          const canvas = await html2canvas(labelElements[j] as HTMLElement);
          canvases.push(canvas);
        }

        const totalHeight = canvases.reduce((sum, canvas) => sum + canvas.height, 0);
        const mergedCanvas = document.createElement('canvas');
        mergedCanvas.width = canvases[0].width;
        mergedCanvas.height = totalHeight;

        const ctx = mergedCanvas.getContext('2d');
        let yOffset = 0;
        canvases.forEach(canvas => {
          if (ctx) {
            ctx.drawImage(canvas, 0, yOffset);
            yOffset += canvas.height;
          }
        });

        const imgData = mergedCanvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      const blob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      const isAllSelected = selectedRows.length === tableData.length;
      
      if (isAllSelected) {
        setTableData([]);
        configureAlert(
          "Print completed successfully. All data has been cleared.",
          () => closeAlert(),
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const remainingData = tableData.filter(item => 
          !selectedRows.includes(item._id || item.tagNumber)
        );
        setTableData(remainingData);
        
        configureAlert(
          `Print completed successfully. ${selectedRows.length} item(s) have been removed.`,
          () => closeAlert(),
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
      setSelectedRows([]);
      setIsPrinting(false);
      setTempLabelsData([]);
    }, 100);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => { },
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const handleAddOrEdit = async (barcode: any) => {
    setIsLoading(true);
    
    try {
      const response: any = await postData("/barcode", barcode);

      if (response.statusCode === 1126) {
        if (barcode.isFirstItem) {
          configureAlert(
            `Barcode has been added successfully.`,
            () => {
              setIsAdding(false);
              closeAlert();
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
        }
      }

      if (response.data && response.data[0]) {
        setTableData((prevTableData: any) => [...prevTableData, response.data[0]]);
      }
      
      return response;
    } catch (error) {
      console.error("Error adding barcode:", error);
      configureAlert(
        "Failed to add barcode. Please try again.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  function handleCancel(): void {
    setIsAdding(false);
  }

  const toggleRowSelection = (item: any) => {
    const rowId = item._id || item.tagNumber;

    setSelectedRows(prev => {
      if (prev.includes(rowId)) {
        return prev.filter(id => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={7} className="spinner-container-incobar">
            <div className="spinner-incobar"></div>
          </td>
        </tr>
      );
    }
    
    if (tableData.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="table-empty-incobar">
            No barcodes added
          </td>
        </tr>
      );
    }
    
    return tableData.map((item, index) => {
      const partDetails = item.partId?.part;
      const rowId = item._id || item.tagNumber;
      const isSelected = selectedRows.includes(rowId);

      return (
        <tr 
          key={index} 
          className={
            isSelected 
              ? "table-row-selected-incobar" 
              : index % 2 === 0 
                ? "table-row-incobar" 
                : "table-row-alt-incobar"
          }
        >
          <td className="table-cell-center-incobar">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleRowSelection(item)}
            />
          </td>
          <td className="table-cell-incobar">{item.tagNumber}</td>
          <td className="table-cell-incobar">{partDetails?.internalPartNo || "N/A"}</td>
          <td className="table-cell-incobar">{partDetails?.partName || "N/A"}</td>
          <td className="table-cell-incobar">{partDetails?.modelId?.name || "N/A"}</td>
          <td className="table-cell-incobar">{partDetails?.categoryId?.name || "N/A"}</td>
          <td className="table-cell-right-incobar">{item.qty}</td>
        </tr>
      );
    });
  };

  return (
    <div className="container-incobar">
      <div className="form-container-incobar">
        <AddIncoBarTagForm
          onSave={handleAddOrEdit}
          onCancel={handleCancel}
          isProcessing={isLoading}
          isEditing={false}
          editingData={null}
          existingTableData={tableData} 
        />
      </div>

      <div className={`table-wrapper-incobar ${tableData.length === 0 && !isLoading ? 'empty-table-container' : ''}`} style={{ padding: 0 }}>
        <table className="table-incobar">
          <thead className="table-header-incobar">
            <tr style={{ height: '24px' }}>
              <th className="table-header-cell-incobar" style={{ width: '30px' }}>
                <input
                  type="checkbox"
                  onChange={() => {
                    if (selectedRows.length === tableData.length) {
                      setSelectedRows([]);
                    } else {
                      setSelectedRows(tableData.map(item => item._id || item.tagNumber));
                    }
                  }}
                  checked={selectedRows.length === tableData.length && tableData.length > 0}
                  disabled={tableData.length === 0}
                  style={{ margin: '1px' }}
                />
              </th>
              <th className="table-header-cell-incobar" style={{ width: '140px' }}>Unique Barcode</th>
              <th className="table-header-cell-incobar" style={{ width: '90px' }}>Part No</th>
              <th className="table-header-cell-incobar" style={{ width: '170px' }}>Part Name</th>
              <th className="table-header-cell-incobar" style={{ width: '110px' }}>Model</th>
              <th className="table-header-cell-incobar" style={{ width: '110px' }}>Category</th>
              <th className="table-header-cell-incobar" style={{ width: '60px' }}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {renderTableContent()}
          </tbody>
        </table>
      </div>

      {permission.menus.barcode.create && (
        <div className="print-button-container-incobar">
          <button
            onClick={handlePrintLabels}
            disabled={selectedRows.length === 0 || isPrinting}
            className={`print-button-incobar ${(selectedRows.length === 0 || isPrinting) ? 'print-button-disabled-incobar' : ''}`}
          >
            {isPrinting ? 'Generating PDF...' : 'Print'}
          </button>
        </div>
      )}

      {isPrinting && (
        <div className="hidden-print-container-incobar" ref={labelsRef}>
          {tempLabelsData.map((data, idx) => (
            <div key={idx} className="label-container-incobar">
              <PartLabelPrint data={data} />
            </div>
          ))}
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default IncoBarTag;